import { APP_CONST } from "../constants/App.const";
import { useAuth } from "../contexts/AuthContext";
import { BookMeetingResult } from "../types/BookMeetingResult.model";
import { Exhibitor } from "../types/Exhibitor.model";
import { Meeting } from "../types/Meeting.model";
import { fetchWithAuth } from "./fetchInterceptor";



export const useMeetingsService = () => {
    const { logout } = useAuth();

    const getExhibitors = async (): Promise<Exhibitor[]> => {
        try {
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/getExhibitors`,
                {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                },
                logout
            );

            if (!response.ok) return [];
            return await response.json();
        } catch {
            return [];
        }
    };

    const getMyMeetings = async (): Promise<Meeting[]> => {
        try {
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/getMyMeetings`,
                {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                },
                logout
            );

            if (!response.ok) return [];
            return await response.json();
        } catch {
            return [];
        }
    };

    const lockMeeting = async (meetingId: number, comment?: string): Promise<BookMeetingResult> => {
        try {
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/lockMeeting`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ meetingId, comment }),
                },

                logout
            );
            const data = await response.json();
            if (!data.success && !data.error) {
                return { success: false, error: "Failed to book meeting. Place try again latter" };
            }
            return data;

        } catch {
            return { success: false, error: "Failed to book meeting. Place try again latter." };
        }
    };

    const unlockMeeting = async (meetingId: number, comment?: string): Promise<boolean> => {
        try {
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/unlockMeeting`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ meetingId, comment }),
                },

                logout
            );

            return response.ok;

        } catch {
            return false;
        }
    };

    const bookMeeting = async (meetingId: number, comment?: string, ignoreOverlap: boolean = false): Promise<BookMeetingResult> => {
        try {
            const url = new URL(`${process.env.REACT_APP_API_URL}/bookMeeting`);
            if (ignoreOverlap) url.searchParams.set("ignoreOverlap", "true");
            const response = await fetchWithAuth(url.toString(),
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ meetingId, comment }),
                },

                logout
            );
            const data = await response.json();
            if (!data.success && !data.error) {
                return { success: false, error: "Failed to book meeting. Place try again latter" };
            }
            return data;

        } catch {
            return { success: false, error: "Failed to book meeting. Place try again latter." };
        }
    };

    const cancelBookedMeeting = async (meetingId: number): Promise<BookMeetingResult> => {
        try {
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/cancelBookedMeeting`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ meetingId }),
                },

                logout
            );
            const data = await response.json();
            if (!data.success && !data.error) {
                return { success: false, error: "Failed to cancel meeting. Place try again latter" };
            }
            return data;
        } catch {
            return { success: false, error: "Failed to cancel meeting. Place try again latter." };
        }
    };

    const getExhibitorDetails = async (exhibitorId: number): Promise<string | null> => {
        try {
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/exhibitorDetails/${exhibitorId}`,
                {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                },

                logout
            );
            const data = await response.json();
            if (!data.success && !data.error) {
                return null;
            }
            return data.details;
        } catch {
            return null;
        }
    };

    const getMeetingIcs = async (meetingId: number): Promise<Blob | null> => {
        try {
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/getMeetingIcs/${meetingId}`,
                {
                    method: "GET",
                    headers: { "Accept": "text/calendar" },
                },

                logout
            );
            if (!response.ok) return null;

            return response.blob();
        } catch {
            return null;
        }
    };

    const getExhibitorLego = async (
        exhibitorId: number
    ): Promise<Blob | null> => {
        try {
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/exhibitorLego/${exhibitorId}`,
                {
                    method: "GET",
                    headers: {
                        Accept: APP_CONST.ALLOWED_IMAGE_TYPES.join(", "),
                    },
                },
                logout
            );

            if (!response.ok) {
                return null;
            }

            return await response.blob();
        } catch (error) {
            console.error("Failed to fetch exhibitor file", error);
            return null;
        }
    };

    const GetMyMeetingsReport = async (): Promise<Blob | null> => {
        try {
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/GetMyMeetingsReport`,
                {
                    method: "GET",
                    headers: {
                        Accept: "application/pdf",
                    },
                },
                logout
            );

            if (!response.ok) {
                return null;
            }

            return await response.blob();
        } catch (error) {
            console.error("Failed to fetch exhibitor file", error);
            return null;
        }
    };

    return {
        getExhibitors,
        getMyMeetings,
        bookMeeting,
        lockMeeting,
        unlockMeeting,
        cancelBookedMeeting,
        getExhibitorDetails,
        getMeetingIcs,
        getExhibitorLego,
        GetMyMeetingsReport
    };
};
